// src/pages/reporteria/financiero/Descuentos.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
  Tag,
  Spin,
  Progress,
  Empty,
  Divider,
  Alert,
  message,
  Select
} from 'antd';
import {
  PercentageOutlined,
  DownloadOutlined,
  TrophyOutlined,
  UserOutlined,
  GiftOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  TagsOutlined,
  FireOutlined,
  TeamOutlined,
  BarChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReporteriaService from '../../../../service/Reporteria';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../utils/exportExcel';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const { RangePicker } = DatePicker;
const { Title: AntTitle, Text, Paragraph } = Typography;
const { Option } = Select;

const Descuentos = () => {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [agruparPor, setAgruparPor] = useState('mes');
  const [activeTab, setActiveTab] = useState('resumen');

  // Estados de datos separados
  const [analisisDescuentos, setAnalisisDescuentos] = useState(null);
  const [evolucionDescuentos, setEvolucionDescuentos] = useState(null);
  const [distribucionPrecios, setDistribucionPrecios] = useState(null);
  const [analisisMatriculas, setAnalisisMatriculas] = useState(null);

  // Cargar datos
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const params = {
        fechaInicio: fechas[0].format('YYYY-MM-DD'),
        fechaFin: fechas[1].format('YYYY-MM-DD')
      };

      const [analisis, evolucion, precios, matriculas] = await Promise.all([
        ReporteriaService.GetAnalisisDescuentos(params),
        ReporteriaService.GetEvolucionDescuentos({ ...params, agruparPor }),
        ReporteriaService.GetDistribucionPorTipoPrecio(params),
        ReporteriaService.GetAnalisisMatriculas(params)
      ]);
    

      setAnalisisDescuentos(analisis);
      setEvolucionDescuentos(evolucion);
      setDistribucionPrecios(precios);
      setAnalisisMatriculas(matriculas);

    } catch (error) {
      console.error('Error cargando datos de descuentos:', error);
      message.error('Error al cargar los datos de descuentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [fechas, agruparPor]);

  // Formatear moneda
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  // Preparar datos para gráfico de línea (evolución)
  const prepararDatosEvolucion = () => {
    if (!evolucionDescuentos?.evolucion) return null;

    const labels = evolucionDescuentos.evolucion.map(item => {
      const fecha = dayjs(item.periodo || item._id);
      if (agruparPor === 'dia') return fecha.format('DD/MM');
      if (agruparPor === 'semana') return `Sem ${fecha.week()}`;
      if (agruparPor === 'mes') return fecha.format('MMM YYYY');
      return item.periodo || item._id;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Total Descuentos',
          data: evolucionDescuentos.evolucion.map(item => item.totalDescuentos),
          borderColor: '#ff4d4f',
          backgroundColor: 'rgba(255, 77, 79, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y'
        },
        {
          label: 'Cantidad Aplicaciones',
          data: evolucionDescuentos.evolucion.map(item => item.cantidadDescuentos),
          borderColor: '#0047bb',
          backgroundColor: 'rgba(24, 144, 255, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1'
        }
      ]
    };
  };

  // Preparar datos para gráfico de dona (tipos de descuento)
  const prepararDatosTiposDescuento = () => {
    if (!analisisDescuentos?.desglosePorTipo) return null;

    const colores = {
      vecino: '#0047bb',
      automatico: '#faad14',
      otro: '#8c8c8c'
    };

    const labels = analisisDescuentos.desglosePorTipo.map(d => {
      const tipo = d._id || 'otro';
      return tipo.charAt(0).toUpperCase() + tipo.slice(1);
    });

    return {
      labels,
      datasets: [
        {
          data: analisisDescuentos.desglosePorTipo.map(d => d.totalDescontado),
          backgroundColor: analisisDescuentos.desglosePorTipo.map(
            d => colores[(d._id)?.toLowerCase()] || colores.otro
          ),
          borderWidth: 3,
          borderColor: '#fff'
        }
      ]
    };
  };

  // Opciones para gráficos
  const opcionesGraficoLinea = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.datasetIndex === 0) {
              return `${context.dataset.label}: ${formatearMoneda(context.parsed.y)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        ticks: {
          callback: (value) => formatearMoneda(value)
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const opcionesGraficoDona = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = formatearMoneda(context.parsed);
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // Columnas para tipos de descuento
  const columnasTiposDescuento = [
    {
      title: 'Tipo de Descuento',
      dataIndex: '_id',
      key: '_id',
      render: (text) => (
        <Tag color={text === 'vecino' ? 'green' : text === 'automatico' ? 'orange' : 'default'} className="capitalize">
          {text || 'N/A'}
        </Tag>
      )
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'center',
      sorter: (a, b) => a.cantidad - b.cantidad
    },
    {
      title: 'Total Descontado',
      dataIndex: 'totalDescontado',
      key: 'totalDescontado',
      align: 'right',
      render: (value) => <Text strong style={{ color: '#ff4d4f' }}>{formatearMoneda(value)}</Text>,
      sorter: (a, b) => a.totalDescontado - b.totalDescontado,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Promedio por Descuento',
      dataIndex: 'promedioDescuento',
      key: 'promedioDescuento',
      align: 'right',
      render: (value) => formatearMoneda(value),
      sorter: (a, b) => a.promedioDescuento - b.promedioDescuento
    },
    {
      title: 'Monto Final Promedio',
      dataIndex: 'montoFinalPromedio',
      key: 'montoFinalPromedio',
      align: 'right',
      render: (value) => <Text style={{ color: '#0047bb' }}>{formatearMoneda(value)}</Text>,
      sorter: (a, b) => a.montoFinalPromedio - b.montoFinalPromedio
    },
    {
      title: '% del Total',
      dataIndex: 'porcentajeDelTotal',
      key: 'porcentajeDelTotal',
      align: 'center',
      render: (value) => (
        <Progress
          percent={parseFloat(value || 0)}
          size="small"
          strokeColor="#ff4d4f"
        />
      ),
      sorter: (a, b) => parseFloat(a.porcentajeDelTotal) - parseFloat(b.porcentajeDelTotal)
    }
  ];

  // Columnas para tipos de precio
  const columnasTiposPrecio = [
    {
      title: 'Tipo de Precio',
      dataIndex: 'tipoPrecio',
      key: 'tipoPrecio',
      render: (text) => (
        <Tag color={text === 'gratuito' ? 'default' : 'green'} className="capitalize">
          {text || 'N/A'}
        </Tag>
      )
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'center',
      render: (value, record) => (
        <Space direction="vertical" size={0} className="text-center">
          <Text strong>{value}</Text>
          <Text type="secondary" className="text-xs">
            {record.porcentajeCantidad}% del total
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.cantidad - b.cantidad
    },
    {
      title: 'Ingresos Totales',
      dataIndex: 'totalIngresos',
      key: 'totalIngresos',
      align: 'right',
      render: (value, record) => (
        <Space direction="vertical" size={0} className="text-right">
          <Text strong style={{ color: '#0047bb' }}>{formatearMoneda(value)}</Text>
          <Text type="secondary" className="text-xs">
            {record.porcentajeIngresos}% del total
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.totalIngresos - b.totalIngresos,
      defaultSortOrder: 'descend'
    },
    {
      title: 'Promedio Ticket',
      dataIndex: 'promedioTicket',
      key: 'promedioTicket',
      align: 'right',
      render: (value) => formatearMoneda(value),
      sorter: (a, b) => a.promedioTicket - b.promedioTicket
    },
    {
      title: 'Rango',
      key: 'rango',
      align: 'center',
      render: (_, record) => (
        <Space direction="vertical" size={0} className="text-xs">
          <Text type="secondary">Min: {formatearMoneda(record.ingresoMinimo)}</Text>
          <Text type="secondary">Max: {formatearMoneda(record.ingresoMaximo)}</Text>
        </Space>
      )
    }
  ];

  // Exportar a CSV
  const exportarCSV = (datos, nombreArchivo) => {
    try {
      ReporteriaService.ExportarReporteACSV(datos, nombreArchivo);
      message.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exportando CSV:', error);
      message.error('Error al exportar el reporte');
    }
  };

  // Exportar a Excel (multi-hoja)
  const exportarExcel = async () => {
    try {
      const hojas = [];

      // Hoja 1: Análisis general por tipo de descuento
      if (analisisDescuentos?.desglosePorTipo?.length) {
        hojas.push({
          nombre: 'Por tipo de descuento',
          columnas: [
            { header: 'Tipo de descuento', key: 'tipo', tipo: 'texto', ancho: 30 },
            { header: 'Cantidad aplicados', key: 'cantidad', tipo: 'numero', total: true },
            { header: 'Total descuento', key: 'total', tipo: 'moneda', total: true },
            { header: 'Descuento promedio', key: 'promedio', tipo: 'moneda' },
            { header: '% del total', key: 'porcentaje', tipo: 'porcentaje' },
          ],
          filas: analisisDescuentos.desglosePorTipo.map(t => ({
            tipo: t.tipoDescuento,
            cantidad: t.cantidadAplicados,
            total: t.totalDescuento,
            promedio: t.promedioDescuento,
            porcentaje: t.porcentajeDelTotal,
          })),
        });
      }

      // Hoja 2: Evolución temporal
      if (evolucionDescuentos?.evolucion?.length) {
        hojas.push({
          nombre: 'Evolución',
          columnas: [
            { header: 'Período', key: 'periodo', tipo: 'texto', ancho: 16 },
            { header: 'Total descuentos', key: 'total', tipo: 'moneda', total: true },
            { header: 'Cantidad de descuentos', key: 'cantidad', tipo: 'numero', total: true },
            { header: 'Descuento promedio', key: 'promedio', tipo: 'moneda' },
          ],
          filas: evolucionDescuentos.evolucion.map(e => ({
            periodo: e._id || e.periodo,
            total: e.totalDescuentos,
            cantidad: e.cantidadDescuentos,
            promedio: e.cantidadDescuentos ? Math.round(e.totalDescuentos / e.cantidadDescuentos) : 0,
          })),
          notas: [`Agrupación: por ${agruparPor}.`],
        });
      }

      // Hoja 3: Distribución por tipo de precio
      if (distribucionPrecios?.distribucion?.length) {
        hojas.push({
          nombre: 'Distribución de precios',
          columnas: [
            { header: 'Tipo de precio', key: 'tipo', tipo: 'texto', ancho: 28 },
            { header: 'Cantidad inscripciones', key: 'cantidad', tipo: 'numero', total: true },
            { header: 'Total ingresos', key: 'ingresos', tipo: 'moneda', total: true },
            { header: 'Total descuentos', key: 'descuentos', tipo: 'moneda', total: true },
            { header: '% del total', key: 'porcentaje', tipo: 'porcentaje' },
          ],
          filas: distribucionPrecios.distribucion.map(d => ({
            tipo: d.tipoPrecio,
            cantidad: d.cantidadInscripciones,
            ingresos: d.totalIngresos,
            descuentos: d.totalDescuentos,
            porcentaje: d.porcentaje,
          })),
        });
      }

      // Hoja 4: Resumen matrículas y descuento vecino
      if (analisisMatriculas) {
        hojas.push({
          nombre: 'Matrículas y vecinos',
          metricas: [
            { metrica: 'Matrículas' },
            { metrica: 'Total de matrículas activas', valor: analisisMatriculas.totalActivas || 0, tipo: 'numero' },
            { metrica: 'Descuento vecino' },
            {
              metrica: 'Inscripciones con descuento vecino',
              valor: analisisMatriculas.inscripcionesConDescuentoVecino || 0,
              tipo: 'numero',
            },
            { metrica: 'Ahorro total otorgado', valor: analisisMatriculas.ahorroTotalVecinos || 0, tipo: 'moneda' },
            {
              metrica: 'Ahorro promedio por inscripción',
              valor: analisisMatriculas.inscripcionesConDescuentoVecino
                ? Math.round(analisisMatriculas.ahorroTotalVecinos / analisisMatriculas.inscripcionesConDescuentoVecino)
                : 0,
              tipo: 'moneda',
            },
          ],
        });
      }

      await exportarLibro({
        archivo: 'descuentos-y-precios',
        titulo: 'Descuentos y precios',
        subtitulo: `Reporte financiero · ${fechas[0].format('DD/MM/YYYY')} al ${fechas[1].format('DD/MM/YYYY')}`,
        usuario: user,
        filtros: {
          'Desde': fechas[0].format('DD/MM/YYYY'),
          'Hasta': fechas[1].format('DD/MM/YYYY'),
          'Agrupación': agruparPor,
        },
        hojas,
      });
      message.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exportando Excel:', error);
      message.error('Error al exportar Excel');
    }
  };

  // Calcular impacto
  const calcularImpacto = () => {
    if (!analisisDescuentos?.resumenGeneral) return { porcentaje: 0, color: '#8c8c8c' };
    
    const impacto = analisisDescuentos.resumenGeneral.impactoFinanciero;
    const porcentaje = parseFloat(impacto?.porcentaje || 0);
    
    let color = '#0047bb'; // Verde (bajo impacto < 15%)
    if (porcentaje > 15 && porcentaje <= 25) color = '#faad14'; // Amarillo (medio impacto)
    if (porcentaje > 25) color = '#ff4d4f'; // Rojo (alto impacto > 25%)
    
    return { porcentaje, color };
  };

  const impacto = calcularImpacto();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <AntTitle level={2} className="mb-2">
          <PercentageOutlined className="mr-2" />
          Análisis de Descuentos
        </AntTitle>
        <Text type="secondary">
          Monitoreo del impacto y efectividad de descuentos aplicados en inscripciones
        </Text>
      </div>

      {/* Filtros */}
      <Card className="mb-6 shadow-sm">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={10}>
            <Space direction="vertical" className="w-full">
              <Text strong>Periodo:</Text>
              <RangePicker
                value={fechas}
                onChange={(dates) => dates && setFechas(dates)}
                format="DD/MM/YYYY"
                className="w-full"
                size="large"
              />
            </Space>
          </Col>

          <Col xs={24} md={8} lg={6}>
            <Space direction="vertical" className="w-full">
              <Text strong>Agrupar evolución por:</Text>
              <Select
                value={agruparPor}
                onChange={setAgruparPor}
                className="w-full"
                size="large"
              >
                <Option value="dia">Día</Option>
                <Option value="semana">Semana</Option>
                <Option value="mes">Mes</Option>
              </Select>
            </Space>
          </Col>

          <Col xs={24} md={4} lg={4}>
            <Button
              type="primary"
              size="large"
              onClick={cargarDatos}
              loading={loading}
              className="w-full mt-6"
              style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
            >
              Actualizar
            </Button>
          </Col>

          <Col xs={24} md={4} lg={4}>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              onClick={exportarExcel}
              disabled={loading || !analisisDescuentos}
              className="w-full mt-6"
            >
              Exportar Excel
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" tip="Cargando datos de descuentos..." />
        </div>
      ) : (
        <>
          {/* KPIs Principales */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Total Descuentos Aplicados"
                  value={analisisDescuentos?.resumenGeneral?.totalDescuentos || 0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                  formatter={(value) => formatearMoneda(value)}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  De {formatearMoneda(analisisDescuentos?.resumenGeneral?.ingresosPotenciales || 0)} potenciales
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Inscripciones con Descuento"
                  value={
                    (analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.vecino || 0) +
                    (analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.automatico || 0)
                  }
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#0047bb' }}
                  suffix={`/ ${analisisDescuentos?.resumenGeneral?.totalInscripciones || 0}`}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  {parseFloat(analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.porcentaje || 0).toFixed(2)}% del total
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Impacto en Ingresos"
                  value={impacto.porcentaje}
                  suffix="%"
                  valueStyle={{ color: impacto.color }}
                  precision={2}
                  prefix={
                    impacto.porcentaje > 25 ? <FallOutlined /> :
                    impacto.porcentaje > 15 ? <RiseOutlined /> : null
                  }
                />
                <Divider className="my-2" />
                <Progress
                  percent={impacto.porcentaje}
                  strokeColor={impacto.color}
                  showInfo={false}
                  size="small"
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card className="shadow-sm">
                <Statistic
                  title="Descuento Promedio"
                  value={analisisDescuentos?.promedios?.descuentoPorInscripcion || 0}
                  prefix="$"
                  valueStyle={{ color: '#722ed1' }}
                  precision={0}
                />
                <Divider className="my-2" />
                <Text type="secondary" className="text-xs">
                  Por inscripción (con y sin descuento)
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Card className="shadow-sm">
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              {/* Tab Resumen */}
              <Tabs.TabPane
                tab={
                  <span>
                    <BarChartOutlined />
                    Resumen
                  </span>
                }
                key="resumen"
              >
                <Row gutter={[16, 16]}>
                  {/* Distribución por tipo */}
                  <Col xs={24} lg={12}>
                    <Card className="bg-gray-50" bordered={false}>
                      <AntTitle level={5} className="mb-4">
                        <TagsOutlined className="mr-2" />
                        Distribución por Tipo de Descuento
                      </AntTitle>
                      <div style={{ height: '350px' }}>
                        {prepararDatosTiposDescuento() ? (
                          <Doughnut
                            data={prepararDatosTiposDescuento()}
                            options={opcionesGraficoDona}
                          />
                        ) : (
                          <Empty description="No hay descuentos aplicados en este periodo" />
                        )}
                      </div>
                    </Card>
                  </Col>

                  {/* Desglose detallado */}
                  <Col xs={24} lg={12}>
                    <Card className="bg-gray-50" bordered={false}>
                      <AntTitle level={5} className="mb-4">
                        <GiftOutlined className="mr-2" />
                        Detalle por Tipo
                      </AntTitle>
                      <div className="space-y-3">
                        {analisisDescuentos?.desglosePorTipo?.map((tipo, index) => (
                          <Card key={index} size="small" hoverable>
                            <Row gutter={16} align="middle">
                              <Col span={10}>
                                <Text strong className="capitalize">
                                  {tipo._id || 'Otro'}
                                </Text>
                                <br />
                                <Tag color="blue" className="text-xs mt-1">
                                  {tipo.cantidad} aplicaciones
                                </Tag>
                              </Col>
                              <Col span={7} className="text-center">
                                <Text type="secondary" className="text-xs">Promedio</Text>
                                <br />
                                <Text strong style={{ fontSize: '14px', color: '#722ed1' }}>
                                  {formatearMoneda(tipo.promedioDescuento)}
                                </Text>
                              </Col>
                              <Col span={7} className="text-right">
                                <Text strong style={{ color: '#ff4d4f', fontSize: '16px' }}>
                                  {formatearMoneda(tipo.totalDescontado)}
                                </Text>
                                <br />
                                <Text type="secondary" className="text-xs">
                                  {tipo.porcentajeDelTotal}% del total
                                </Text>
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        
                        {(!analisisDescuentos?.desglosePorTipo || 
                          analisisDescuentos?.desglosePorTipo.length === 0) && (
                          <Empty 
                            description="No hay descuentos aplicados en este periodo"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        )}
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Tabs.TabPane>

              {/* Tab Evolución */}
              <Tabs.TabPane
                tab={
                  <span>
                    <LineChartOutlined />
                    Evolución
                  </span>
                }
                key="evolucion"
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <AntTitle level={5} className="mb-4">
                      Evolución de Descuentos en el Tiempo
                    </AntTitle>
                    <div style={{ height: '400px' }}>
                      {prepararDatosEvolucion() ? (
                        <Line data={prepararDatosEvolucion()} options={opcionesGraficoLinea} />
                      ) : (
                        <Empty description="No hay datos de evolución para mostrar" />
                      )}
                    </div>
                  </Col>
                  
                  {/* Tabla de evolución */}
                  {evolucionDescuentos?.evolucion && (
                    <Col span={24} className="mt-4">
                      <div className="mb-4 flex justify-between items-center">
                        <AntTitle level={5}>Detalle de Evolución</AntTitle>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => exportarCSV(evolucionDescuentos.evolucion, 'evolucion_descuentos')}
                        >
                          Exportar CSV
                        </Button>
                      </div>
                      <Table
                        dataSource={evolucionDescuentos.evolucion}
                        rowKey={(record) => record.periodo || record._id}
                        pagination={false}
                        scroll={{ x: 1000 }}
                        columns={[
                          {
                            title: 'Periodo',
                            dataIndex: 'periodo',
                            key: 'periodo',
                            render: (text) => {
                              const fecha = dayjs(text);
                              if (agruparPor === 'dia') return fecha.format('DD/MM/YYYY');
                              if (agruparPor === 'mes') return fecha.format('MMMM YYYY');
                              return text;
                            }
                          },
                          {
                            title: 'Total Descuentos',
                            dataIndex: 'totalDescuentos',
                            key: 'totalDescuentos',
                            align: 'right',
                            render: (value) => <Text strong style={{ color: '#ff4d4f' }}>{formatearMoneda(value)}</Text>
                          },
                          {
                            title: 'Vecino',
                            dataIndex: 'descuentoVecino',
                            key: 'descuentoVecino',
                            align: 'right',
                            render: (value) => formatearMoneda(value)
                          },
                          {
                            title: 'Automático',
                            dataIndex: 'descuentoAutomatico',
                            key: 'descuentoAutomatico',
                            align: 'right',
                            render: (value) => formatearMoneda(value)
                          },
                          {
                            title: 'Inscripciones',
                            dataIndex: 'cantidadInscripciones',
                            key: 'cantidadInscripciones',
                            align: 'center'
                          },
                          {
                            title: 'Con Descuento',
                            dataIndex: 'inscripcionesConDescuento',
                            key: 'inscripcionesConDescuento',
                            align: 'center',
                            render: (value, record) => (
                              <Space direction="vertical" size={0} className="text-center">
                                <Text strong>{value}</Text>
                                <Text type="secondary" className="text-xs">
                                  {record.porcentajeConDescuento.toFixed(1)}%
                                </Text>
                              </Space>
                            )
                          },
                          {
                            title: '% Impacto',
                            dataIndex: 'porcentajeDescuentoSobreIngresos',
                            key: 'porcentajeDescuentoSobreIngresos',
                            align: 'center',
                            render: (value) => (
                              <Tag color={value > 25 ? 'red' : value > 15 ? 'orange' : 'green'}>
                                {value.toFixed(2)}%
                              </Tag>
                            )
                          }
                        ]}
                      />
                    </Col>
                  )}
                </Row>
              </Tabs.TabPane>

              {/* Tab Tipos de Descuento */}
              <Tabs.TabPane
                tab={
                  <span>
                    <TagsOutlined />
                    Tipos de Descuento
                  </span>
                }
                key="tipos"
              >
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <AntTitle level={5}>Análisis Detallado por Tipo de Descuento</AntTitle>
                    <Text type="secondary">
                      Desglose completo de descuentos aplicados según su tipo
                    </Text>
                  </div>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportarCSV(
                      analisisDescuentos?.desglosePorTipo || [], 
                      'tipos_descuento'
                    )}
                  >
                    Exportar CSV
                  </Button>
                </div>
                <Table
                  columns={columnasTiposDescuento}
                  dataSource={analisisDescuentos?.desglosePorTipo || []}
                  rowKey={(record) => record._id}
                  pagination={false}
                  locale={{
                    emptyText: 'No hay descuentos aplicados en este periodo'
                  }}
                />
              </Tabs.TabPane>

              {/* Tab Tipos de Precio */}
              <Tabs.TabPane
                tab={
                  <span>
                    <DollarOutlined />
                    Tipos de Precio
                  </span>
                }
                key="precios"
              >
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <AntTitle level={5}>Distribución por Tipo de Precio</AntTitle>
                    <Text type="secondary">
                      Análisis de inscripciones según el tipo de precio aplicado (particular, vecino, gratuito)
                    </Text>
                  </div>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportarCSV(
                      distribucionPrecios?.distribucion || [], 
                      'distribucion_precios'
                    )}
                  >
                    Exportar CSV
                  </Button>
                </div>
                
                {/* Cards con totales */}
                <Row gutter={[16, 16]} className="mb-4">
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small" className="bg-blue-50">
                      <Statistic
                        title="Ingresos Totales"
                        value={distribucionPrecios?.totales?.totalIngresos || 0}
                        prefix={<DollarOutlined />}
                        valueStyle={{ color: '#0047bb' }}
                        formatter={(value) => formatearMoneda(value)}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small" className="bg-blue-50">
                      <Statistic
                        title="Total Inscripciones"
                        value={distribucionPrecios?.totales?.totalInscripciones || 0}
                        prefix={<UserOutlined />}
                        valueStyle={{ color: '#0047bb' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <Card size="small" className="bg-purple-50">
                      <Statistic
                        title="Ticket Promedio"
                        value={distribucionPrecios?.totales?.promedioGeneral || 0}
                        prefix="$"
                        valueStyle={{ color: '#722ed1' }}
                        precision={0}
                      />
                    </Card>
                  </Col>
                </Row>
                
                <Table
                  columns={columnasTiposPrecio}
                  dataSource={distribucionPrecios?.distribucion || []}
                  rowKey={(record) => record.tipoPrecio || record._id}
                  pagination={false}
                  locale={{
                    emptyText: 'No hay datos de precios para mostrar'
                  }}
                />
              </Tabs.TabPane>
            </Tabs>
          </Card>

          {/* Insights y Recomendaciones */}
          <Row gutter={[16, 16]} className="mt-6">
            <Col xs={24} lg={12}>
              <Card
                className="shadow-sm"
                title={
                  <Space>
                    <FireOutlined style={{ color: '#faad14' }} />
                    <span>Insights</span>
                  </Space>
                }
              >
                <Space direction="vertical" className="w-full" size="middle">
                  {impacto.porcentaje > 25 && (
                    <Alert
                      message="Alto Impacto en Ingresos"
                      description={`Los descuentos representan el ${impacto.porcentaje.toFixed(2)}% del potencial de ingresos. Se recomienda revisar las políticas de descuento para optimizar la rentabilidad.`}
                      type="warning"
                      showIcon
                    />
                  )}
                  
                  {impacto.porcentaje > 15 && impacto.porcentaje <= 25 && (
                    <Alert
                      message="Impacto Moderado"
                      description={`Los descuentos representan el ${impacto.porcentaje.toFixed(2)}% del potencial de ingresos. Se encuentra en un nivel aceptable pero es importante monitorearlo.`}
                      type="info"
                      showIcon
                    />
                  )}

                  {impacto.porcentaje <= 15 && impacto.porcentaje > 0 && (
                    <Alert
                      message="Impacto Controlado"
                      description={`Los descuentos representan solo el ${impacto.porcentaje.toFixed(2)}% del potencial de ingresos. El sistema de descuentos está bien balanceado.`}
                      type="success"
                      showIcon
                    />
                  )}
                  
                  <Card size="small" className="bg-blue-50 border-blue-200">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Descuento por Inscripción"
                          value={analisisDescuentos?.promedios?.descuentoPorInscripcion || 0}
                          prefix="$"
                          valueStyle={{ color: '#ff4d4f', fontSize: '16px' }}
                          precision={0}
                        />
                        <Text type="secondary" className="text-xs">
                          Promedio considerando todas
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Ingreso por Inscripción"
                          value={analisisDescuentos?.promedios?.ingresoPorInscripcion || 0}
                          prefix="$"
                          valueStyle={{ color: '#0047bb', fontSize: '16px' }}
                          precision={0}
                        />
                        <Text type="secondary" className="text-xs">
                          Ingreso real promedio
                        </Text>
                      </Col>
                    </Row>
                  </Card>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                className="shadow-sm"
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#0047bb' }} />
                    <span>Métricas Clave</span>
                  </Space>
                }
              >
                <Space direction="vertical" className="w-full" size="small">
                  <Card size="small" className="bg-gray-50">
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space>
                          <Text strong>Descuento Vecino</Text>
                          <Tag color="blue">
                            {analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.vecino || 0} inscrip.
                          </Tag>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong style={{ color: '#0047bb', fontSize: '16px' }}>
                          {formatearMoneda(
                            evolucionDescuentos?.evolucion?.[0]?.descuentoVecino || 0
                          )}
                        </Text>
                      </Col>
                    </Row>
                  </Card>
                  
                  <Card size="small" className="bg-gray-50">
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space>
                          <Text strong>Descuento Automático</Text>
                          <Tag color="orange">
                            {analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.automatico || 0} inscrip.
                          </Tag>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong style={{ color: '#faad14', fontSize: '16px' }}>
                          {formatearMoneda(
                            evolucionDescuentos?.evolucion?.[0]?.descuentoAutomatico || 0
                          )}
                        </Text>
                      </Col>
                    </Row>
                  </Card>
                  
                  <Card size="small" className="bg-gray-50">
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text strong>Porcentaje con Descuento</Text>
                      </Col>
                      <Col>
                        <Tag color="blue" style={{ fontSize: '14px' }}>
                          {parseFloat(
                            analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.porcentaje || 0
                          ).toFixed(2)}%
                        </Tag>
                      </Col>
                    </Row>
                  </Card>
                  
                  <Card size="small" className="bg-gray-50">
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Text strong>Total Inscripciones</Text>
                      </Col>
                      <Col>
                        <Space size="small">
                          <Text type="secondary">
                            {(analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.vecino || 0) +
                             (analisisDescuentos?.resumenGeneral?.inscripcionesConDescuento?.automatico || 0)}
                          </Text>
                          <Text type="secondary">/</Text>
                          <Text strong style={{ color: '#0047bb', fontSize: '16px' }}>
                            {analisisDescuentos?.resumenGeneral?.totalInscripciones || 0}
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                  
                  <Divider className="my-3" />
                  
                  <Paragraph className="mb-0 text-xs text-gray-600">
                    💡 <strong>Tip:</strong> Un impacto entre 10-20% es considerado óptimo para balancear accesibilidad con rentabilidad.
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Descuentos;
